import { Eye, Download, AlertCircle, TrendingUp, Users, Calendar } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useServiceChargeDistribution } from '@/hooks/serviceCharge/useServiceChargeDistribution';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { formatCurrencyAmount } from '@/lib/currencyFormat';

interface ServiceChargeDistributionPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodId: string;
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
}

export const ServiceChargeDistributionPreview = ({
  open,
  onOpenChange,
  periodId,
  periodLabel,
  periodStart,
  periodEnd,
}: ServiceChargeDistributionPreviewProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { currencyCode, isLoading: currencyLoading } = useWorkplaceCurrency();
  
  const { data, isLoading: distributionLoading, error, refetch } = useServiceChargeDistribution(
    periodId,
    new Date(periodStart),
    new Date(periodEnd),
    open
  );

  const isLoading = distributionLoading || currencyLoading;

  const filteredEmployees = data?.employees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const formatCurrency = (amount: number) => formatCurrencyAmount(amount, currencyCode);

  const formatNumber = (num: number, decimals: number = 1) => {
    return num.toFixed(decimals);
  };

  const handleExport = () => {
    if (!data) return;

    const csvEscape = (val: string) => {
      const needsQuote = /[",\n]/.test(val);
      const escaped = val.replace(/"/g, '""');
      return needsQuote ? `"${escaped}"` : escaped;
    };

    const csvContent = [
      ['Employee Name', 'Working Days', 'Weight Category', 'Multiplier', 'Weighted Days', 'Currency', 'Distribution Amount', '% of Pool'],
      ...data.employees.map(emp => [
        emp.name,
        emp.workingDays.toString(),
        emp.weightLabel,
        emp.weightMultiplier.toString(),
        formatNumber(emp.weightedDays),
        currencyCode,
        Math.round(emp.distributionAmount).toString(),
        formatNumber(emp.percentOfPool, 2),
      ]),
      [],
      ['Summary'],
      ['Total Working Days', data.summary.totalWorkingDays.toString()],
      ['Total Weighted Days', formatNumber(data.summary.totalWeightedDays)],
      ['Currency', currencyCode],
      ['Staff Allocation Amount', Math.round(data.summary.staffAllocationAmount).toString()],
      ['Total Distributed', Math.round(data.summary.totalDistributed).toString()],
    ].map(row => row.map(csvEscape).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `service-charge-distribution-${periodLabel.replace(/\s+/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Distribution Preview: {periodLabel}
          </DialogTitle>
          <DialogDescription>
            Preview how the service charge pool will be distributed among assigned employees
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {isLoading && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <Skeleton key={i} className="h-24" />
                ))}
              </div>
              <Skeleton className="h-64" />
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to load distribution preview. Please try again.
              </AlertDescription>
            </Alert>
          )}

          {data && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total Working Days
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatNumber(data.summary.totalWorkingDays)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total Weighted Days
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatNumber(data.summary.totalWeightedDays)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Employees Assigned
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      {data.summary.employeesAssigned}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Staff Allocation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-bold text-primary">
                      {formatCurrency(data.summary.staffAllocationAmount)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Warnings */}
              {data.warnings.length > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-disc list-inside space-y-1">
                      {data.warnings.map((warning, idx) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Search */}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="max-w-sm"
                />
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              {/* Distribution Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee Name</TableHead>
                      <TableHead className="text-right">Working Days</TableHead>
                      <TableHead>Weight Category</TableHead>
                      <TableHead className="text-right">Multiplier</TableHead>
                      <TableHead className="text-right">Weighted Days</TableHead>
                      <TableHead className="text-right">Distribution Amount</TableHead>
                      <TableHead className="text-right">% of Pool</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEmployees.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">
                          {searchTerm ? 'No employees found' : 'No employees assigned'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredEmployees.map((emp) => (
                        <TableRow
                          key={emp.profileId}
                          className={emp.workingDays === 0 ? 'bg-muted/50' : ''}
                        >
                          <TableCell className="font-medium">{emp.name}</TableCell>
                          <TableCell className="text-right">
                            {formatNumber(emp.workingDays)}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {emp.weightLabel}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(emp.weightMultiplier)}×
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(emp.weightedDays)}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(emp.distributionAmount)}
                          </TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">
                            {formatNumber(emp.percentOfPool, 2)}%
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Footer Summary */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-muted-foreground">
                    Showing {filteredEmployees.length} of {data.employees.length} employees
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Total Distributed</div>
                    <div className="text-xl font-bold text-primary">
                      {formatCurrency(data.summary.totalDistributed)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
